import React, { useState } from 'react';
import { useLayoutContext } from 'contexts/LayoutContext';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import PageSection from 'components/PageSection';
import BankReconciliationWarningBox from '../BankReconciliationWarning';
import { CalendarDay } from 'further-ui/utils';
import { AppDatePicker, FieldRow } from 'components/FormElements';
import { Row } from 'components/Layout';
import Button from 'components/Button';
import api from 'lib/trpcClient';
import TransactionAllocator from 'components/TransactionAllocator';
import Spacing from 'components/Spacing';
import PageLoader from 'components/PageLoader';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import CurrencyRate from 'components/CurrencyRate';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import ButtonGroup from 'components/ButtonGroup';
import { ActionRefLabels } from 'further-ui/labels';
import { Alert } from '@mui/material';
import { useDebounce } from 'usehooks-ts';
import { useDisclosure } from 'further-ui/hooks';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';

const ApproveTransactionRates: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const [viewDate, setViewDate] = useState<CalendarDay | null>(
    new CalendarDay(new Date()),
  );
  const [overrideRates, setOverrideRates] = useState<
    Record<string, CurrencyRateDetails>
  >({});

  const { hasEqualisationInDraftState } = useEqualisations(vehicle?._id ?? '');
  const confirmDialog = useDisclosure();
  const { selectedVehicleId } = useLayoutContext();
  const { latestApprovedEqualisationDate } = useEqualisations(
    selectedVehicleId ?? '',
  );
  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const handleViewDateChange = (date: Date | null) => {
    setViewDate(date ? new CalendarDay(date) : null);
  };

  const debouncedViewDate = useDebounce(viewDate, 800);

  const { data: actions, isLoading } =
    api.transactionFx.listActionsForRateApproval.useQuery(
      {
        vehicleId: vehicle?._id ?? '',
        date: debouncedViewDate ?? new CalendarDay(new Date()),
      },
      { enabled: !!debouncedViewDate && !!vehicle },
    );

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: approveFxRates, isPending } =
    api.transactionFx.approveFxRates.useMutation(
      mutationOpts({
        successMessage: 'Transaction rates approved successfully.',
      }),
    );

  const handleApproveFxRates = () => {
    if (!actions?.length) return;

    approveFxRates({
      vehicleId: vehicle?._id ?? '',
      actions: actions.map((action) => ({
        id: action.id,
        currencyRate: overrideRates[action.id] ?? action.currencyRate ?? null,
      })),
    });
  };

  if (!vehicle || transactionTypesLoading) {
    return <PageLoader />;
  }

  return (
    <PageContainer
      heading="Automated Transactions: Approve transaction rates"
      loading={isLoading}
    >
      <PageContent withSpacing>
        <BankReconciliationWarningBox
          date={debouncedViewDate?.toUTCDate() ?? null}
          warningText="There are pending transactions awaiting bank reconciliation. Click below to process these before reviewing accruals, as reconciled transactions may change outstanding accruals."
        />
        <PageSection>
          <Row centered spacing="md">
            <FieldRow title="Approve transaction rates to date" centerTitle>
              <AppDatePicker
                placeholder="Select date"
                value={viewDate?.toUTCDate() ?? null}
                onChange={(date) => {
                  handleViewDateChange(date);
                }}
                disableFuture
                minDate={latestApprovedEqualisationDate}
              />
            </FieldRow>
          </Row>
        </PageSection>
        <PageSection withSpacing>
          {actions?.length ? (
            actions.map((action) => (
              <Spacing key={action.id}>
                <CurrencyRate
                  currency={action.currencyCode}
                  date={action.ledgerDate}
                  vehicle={vehicle}
                  fxRate={
                    overrideRates[action.id] ?? action.currencyRate ?? null
                  }
                  isFetchingRate={false}
                  onRateChange={(rate) => {
                    setOverrideRates((prev) => ({
                      ...prev,
                      [action.id]: rate,
                    }));
                  }}
                />
                <TransactionAllocator
                  title={`${ActionRefLabels[action.actionRef]}: ${
                    action.counterpartyLabel
                  } on ${action.ledgerDate.toLabel()}`}
                  transactions={action.transactions}
                  transactionCurrency={action.currencyCode}
                  vehicle={vehicle}
                  fxRate={
                    overrideRates[action.id] ?? action.currencyRate ?? null
                  }
                  transactionTypeOptions={transactionTypeOptions}
                  readOnly
                  forceLcyCalculation
                />
              </Spacing>
            ))
          ) : (
            <Alert severity="info-light">
              No transactions found for selected date.
            </Alert>
          )}
        </PageSection>
        {!!actions?.length && (
          <ButtonGroup>
            <Button
              variant="contained"
              color="primary"
              onClick={
                hasEqualisationInDraftState
                  ? confirmDialog.onOpen
                  : handleApproveFxRates
              }
              loading={isPending}
            >
              Confirm and proceed
            </Button>
          </ButtonGroup>
        )}
        {hasEqualisationInDraftState && (
          <PendingDraftEqualisationsModal
            isOpen={confirmDialog.isOpen}
            onClose={confirmDialog.onClose}
            onConfirm={handleApproveFxRates}
            loading={isPending}
          />
        )}
      </PageContent>
    </PageContainer>
  );
};

export default ApproveTransactionRates;
